<template>
    <div
        class="sbs-input"
        :class="inputClasses"
        :style="$attrs.style"
    >
        <div
            class="sbs-input__wrapper trs"
            :class="inputWrapperClasses"
            @click="inputWrapperClickHandler">
            <!-- @slot Слот перед инпутом -->
            <slot name="prepend"></slot>

            <div class="sbs-input__field-wrapper">
                <input
                    v-bind="inputAttrs"
                    ref="input"
                    v-imask="inputMask"
                    :id="inputId"
                    class="sbs-input__field trs"
                    :class="[{'sbs-input__field--inherit-text-color': inheritTextColor}, inputFieldClass]"
                    :autocomplete="autocomplete"
                    :disabled="disabled"
                    :style="inputFieldStyles"
                    :type="type"
                    :value="modelValue"
                    v-on="inputFieldListeners">

                <label
                    v-if="label"
                    class="sbs-input__label trs"
                    :class="inputLabelClasses"
                    :for="inputId"
                    :title="focused && label">
                    <span class="sbs-input__label-text text-clamp trs">
                        {{ label }}
                    </span>
                </label>

                <transition name="fade">
                    <span
                        v-if="isFaderVisible"
                        class="sbs-input__field-fader trs"
                        :class="inputFaderClasses"></span>
                </transition>
            </div>

            <!-- @slot Слот после инпута -->
            <slot name="append">
                <SbsButtonIcon
                    v-if="isPasswordInput"
                    v-bind="passwordTogglerData"
                    class="sbs-input__password-toggler"
                    @click.stop="togglePasswordVisibility" />
            </slot>
        </div>

        <!--
            @slot Слот для сообщений об ошибках.
            Доступны пропсы `rules`, `valid`, и `value`
        -->
        <slot
            name="error"
            v-bind="{
                rules,
                valid,
                value: modelValue,
            }">
            <div
                v-if="isShowError"
                class="sbs-input__error-message">
                <SbsIcon
                    v-bind="errorIconData"
                    class="sbs-input__error-icon" />
                {{ errorMessage || defaultErrorMessage }}
            </div>
        </slot>
    </div>
</template>

<script>
import { IMaskDirective } from 'vue-imask';
import ResizeObserver from '../../utils/resizeObserver.js';
import themesMixin from '../../mixins/themes.js';
import validationMixin from '../../mixins/validation.js';
import { AllColors } from '../../utils/colors.js';
import {
    getRandomId,
    converToUnit,
} from '../../utils/helpers.js';
import {
    AllSizes,
    INPUT_SIZES,
} from '../../utils/sizes.js';
import { isArrayContainsValue } from '../../utils/validators.js';
import { AllIconGroups } from '../icon/utils/consts.js';
import {
    InputTypes,
    INPUT_TYPES,
} from './utils/consts.js';

/**
 * Компонент инпут
 */
export default {
    name: 'SbsInput',
    emits: ['update:modelValue', 'focus', 'blur', 'input', 'valid', 'invalid'],
    directives: {
        imask: IMaskDirective,
    },
    mixins: [themesMixin, validationMixin],
    data() {
        return {
            dirty: this.initialDirty,
            focused: false,
            isPasswordVisible: false,
            inputWidth: {
                offsetWidth: null,
                scrollWidth: null,
            },
            resizeObserver: null,
        };
    },
    computed: {
        isShowError() {
            return this.dirty && this.invalid;
        },
        inputId() {
            return this.id || `sbs-input-${getRandomId()}`;
        },
        inputClasses() {
            return [
                this.$attrs.class,
                this.size,
                {
                    'empty': !this.modelValue,
                    'disabled': this.disabled,
                    'focused': this.focused,
                    'invalid': this.isShowError,
                    'full-width': this.fullWidth,
                    'fader': this.faderVisible,
                    'auto-grow': this.autoGrow,
                    'time-mask': this.inputMask,
                    'empty-label': this.isEmptyLabel,
                },
            ];
        },
        inputAttrs() {
            const { class: _, ...attrs } = this.$attrs;

            return attrs;
        },
        inputWrapperClasses() {
            return [...this.themeClasses, this.inputWrapperClass];
        },
        isEmptyLabel() {
            return this.label === '';
        },
        inputFieldStyles() {
            const styles = {};

            if (this.autoGrow) {
                const width = this.inputWidth.scrollWidth ?? 0;

                styles.width = converToUnit(width);
            }

            return styles;
        },
        inputFieldListeners() {
            return {
                ...this.$attrs,
                focus: this.inputFocusHandler,
                blur: this.inputBlurHandler,
                input: this.inputInputHandler,
                accept: this.inputAcceptHandler,
            };
        },
        inputLabelClasses() {
            const isLabelTop = this.focused || this.modelValue;

            return [
                this.inputLabelClass,
                {
                    'sbs-input__label--top': isLabelTop,
                },
            ];
        },
        inputFaderClasses() {
            return [this.inputFaderClass];
        },
        isFaderVisible() {
            const { offsetWidth, scrollWidth } = this.inputWidth;

            return (
                !this.disabled &&
                !this.focused &&
                this.faderVisible &&
                (scrollWidth > offsetWidth)
            );
        },
        isPasswordInput() {
            return this.type === InputTypes.PASSWORD;
        },
        passwordTogglerData() {
            return {
                disabled: this.disabled,
                iconName: this.isPasswordVisible ? 'eye-slash' : 'eye',
                colorTheme: AllColors.GRAY_DEEP,
                hoverColor: AllColors.BLACK,
                width: 20,
                height: 20,
            };
        },
        errorIconData() {
            return {
                iconName: 'exclamation',
                iconGroup: AllIconGroups.SOLID,
                iconSize: AllSizes.SMALL,
                colorTheme: AllColors.WHITE,
                backgroundTheme: AllColors.GRADIENT_PINK,
            };
        },
    },
    methods: {
        inputWrapperClickHandler() {
            this.$nextTick(() => {
                const { input } = this.$refs;

                if (!input) return;

                input.focus();

                if (this.inputMask && input.maskRef && !this.modelValue) {
                    input.maskRef.updateCursor(0);
                }
            });
        },
        async calcInputWidth() {
            this.inputWidth.offsetWidth = this.$refs.input.offsetWidth;

            if (this.autoGrow) {
                this.inputWidth.scrollWidth = null;

                await this.$nextTick();
            }

            this.inputWidth.scrollWidth = this.$refs.input.scrollWidth;
        },
        async inputFocusHandler(e) {
            this.focused = true;

            /**
             * Событие `focus` срабатывает при фокусе в поле инпута.
             * В качестве параметра - объект события
             */
            this.$emit('focus', e);

            await this.calcInputWidth();
        },
        inputBlurHandler(e) {
            this.focused = false;
            this.dirty = true;

            /**
             * Событие `blur` срабатывает при потере фокуса инпутом.
             * В качестве параметра - объект события
             */
            this.$emit('blur', e);
        },
        async inputInputHandler(event) {
            /**
             * Если есть маска, то эмит значения будет в событии `inputAcceptHandler`
             */
            if (this.inputMask) return;

            const { value } = event.target;

            /**
             * Эмит события `update:modelValue` с текущей введенной строкой
             */
            this.$emit('update:modelValue', value);

            await this.calcInputWidth();
        },
        async inputAcceptHandler(event) {
            let { value, masked } = event.detail;
            const { rawInputValue } = masked;

            if (!rawInputValue) value = '';

            /**
             * Эмит события `input` с текущей введенной строкой
             */
            this.$emit('update:modelValue', value, event);

            await this.calcInputWidth();
        },
        togglePasswordVisibility() {
            this.isPasswordVisible = !this.isPasswordVisible;
            this.$refs.input.type = this.isPasswordVisible
                ? 'text'
                : 'password';
        },
        async update() {
            await this.$nextTick();
            await this.calcInputWidth();
        },
        setInputCssVarTheme(colorTheme) {
            if (!colorTheme) return;

            this.$el.style.setProperty(
                '--input-color-theme',
                `var(--${colorTheme})`,
            );
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Автозаполнение инпута
         *
         * Валидные значения: `on` | `off` | `<autofill_token>` ([документация](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete))
         */
        autocomplete: {
            type: String,
            default: 'on',
        },
        /**
         * Определяет будет ли инпут увеличивать ширину, когда текст превышает размер.
         */
        autoGrow: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет наличие бордера у кнопки
         */
        // eslint-disable-next-line vue/no-unused-properties
        bordered: {
            type: Boolean,
            default: true,
        },
        /**
         * Перевод инпута в неактивное состояние
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Сообщение при ошибке
         */
        errorMessage: {
            type: String,
            default: '',
        },
        /**
         * Определяет будет ли видно затухание при превышении длины текста длины инпута
         */
        faderVisible: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет будет ли инпут занимать всю доступную ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета бордера при наведении
         */
        // eslint-disable-next-line vue/no-unused-properties
        hoverBorder: {
            type: String,
            default: AllColors.CURRENT_COLOR,
        },
        /**
         * Уникальный идентификатор инпута
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Класс для затухания инпута
         */
        inputFaderClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для инпута
         */
        inputFieldClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для лейбла
         */
        inputLabelClass: {
            type: String,
            default: '',
        },
        /**
         * Добавление маски для инпута.
         * Используется библиотека [vue-imask](https://github.com/uNmAnNeR/imaskjs)
         */
        inputMask: {
            type: Object,
            default: null,
        },
        /**
         * Класс для обертки инпута
         */
        inputWrapperClass: {
            type: String,
            default: '',
        },
        /**
         * Лейбл для инпута
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Размер инпута
         */
        size: {
            type: String,
            default: AllSizes.MEDIUM,
            validator: isArrayContainsValue(INPUT_SIZES),
        },
        /**
         * Тип инпута
         */
        type: {
            type: String,
            default: InputTypes.TEXT,
            validator: isArrayContainsValue(INPUT_TYPES),
        },
        /**
         * Значение, введенное в инпут
         * @model
         */
        modelValue: {
            type: String,
            default: '',
        },
        inheritTextColor: {
            type: Boolean,
            default: false,
        },
        initialDirty: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        colorTheme(val) {
            this.setInputCssVarTheme(val);
        },
        modelValue: {
            immediate: true,
            handler(value) {
                if (!this.noValidate) {
                    this.validate(value);
                }
            },
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.update);
    },
    async mounted() {
        this.resizeObserver.observe(this.$el);

        await this.$nextTick();
        await this.update();
        this.setInputCssVarTheme(this.colorTheme);
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

<style lang="scss">
.sbs-input {
    --input-color-theme: #{$success};

    position: relative;
    display: inline-flex;
    flex-direction: column;
    max-width: 100%;

    &.full-width {
        width: 100%;
    }

    &.auto-grow {
        align-items: flex-start;
    }

    &__wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        max-width: 100%;
        height: 100%;
        padding: 0 20px;
        color: var(--input-color-theme);
        background-color: $white;
        border-color: $gray;
        border-radius: $rounded-10;
        cursor: text;
        transition-property: border-color;

        .sbs-input.auto-grow & {
            width: auto;
        }

        .sbs-input:not(.disabled).focused & {
            border-color: $current-color;
        }

        .sbs-input.disabled & {
            background-color: $gray-ultra-light;
            border-color: $gray;
            cursor: not-allowed;
            user-select: none;

            & > * {
                pointer-events: none;
            }
        }

        .sbs-input:not(.disabled).invalid & {
            border-color: $danger;
        }
    }

    &__field-wrapper {
        position: relative;
        display: flex;
        width: 100%;
    }

    &__field {
        width: 100%;
        min-width: 1px;
        margin: 0;
        color: $black;
        font-family: inherit;
        background-color: $transparent;
        border: none;
        outline: none;
        transition-property: background-color, opacity, font;

        @include typo-secondary-book;
        @include autofill;

        .sbs-input.small & {
            padding: 8px 0;
        }

        .sbs-input.medium & {
            padding: 14px 0;
        }

        .sbs-input.empty &,
        &::placeholder {
            color: $gray-deep;
        }

        .sbs-input:not(.focused):not(.empty-label).empty & {
            opacity: 0;
        }

        .sbs-input.disabled & {
            opacity: 0.4;
        }

        .sbs-input.auto-grow & {
            width: 0;
        }

        &--inherit-text-color {
            color: inherit;
        }
    }

    &__label {
        position: absolute;
        top: 50%;
        right: -5px;
        left: -5px;
        z-index: 1;
        display: flex;
        color: $gray-deep;
        transform: translateY(-50%);
        cursor: text;
        transition-timing-function: linear;

        @include typo-secondary-book;

        .sbs-input:not(.empty) &,
        .sbs-input.focused &,
        .sbs-input__field:-webkit-autofill + & {
            top: -1px;
        }

        &--top,
        .sbs-input__field:-webkit-autofill + & {
            @include typo-subsecondary-demi;
        }
    }

    &__label-text {
        padding-right: 5px;
        padding-left: 5px;
        border-radius: $rounded-10;
        transition-property: background-color;

        .sbs-input__label--top &,
        .sbs-input__field:-webkit-autofill + .sbs-input__label & {
            background-color: $white;
        }

        .sbs-input.empty.disabled & {
            opacity: 0.4;
        }

        .sbs-input:not(.empty).disabled & {
            color: rgba($gray-deep, 0.4);
            background-image:
                linear-gradient(
                    180deg,
                    $white 0%,
                    $white 45%,
                    $gray-ultra-light 50%,
                    $gray-ultra-light 100%
                );
        }
    }

    &__field-fader {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 30px;
        background-image: linear-gradient(90deg, rgba($white, 0) 0%, $white 100%);
        content: "";
        pointer-events: none;
    }

    &__password-toggler {
        margin-left: 15px;
    }

    &__error-message {
        margin-top: 8px;
        color: $danger;

        @include typo-subsecondary-demi;
    }

    &__error-icon {
        width: 15px;
        height: 15px;
        margin-right: 5px;
        border-radius: $rounded-circle;
    }
}
</style>
